// TotalStateAdminCommissionReport.jsx

import React, { useEffect, useState, useRef } from 'react';
import {
  Button,
  Box,
  CircularProgress,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Paper,
  Backdrop,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import { DataGrid } from '@mui/x-data-grid';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { format } from 'date-fns';
import BackButton from '../../components/BackButton';
// [CASH-MODE] UPI / Cash filter + indication
import PaymentModeFilter from '../../components/PaymentModeFilter';
import PaymentModeChip from '../../components/PaymentModeChip';
import PaymentModeTotals from '../../components/PaymentModeTotals';
import { PAYMENT_MODES } from '../../constants/paymentModes';
import { paymentModeParam, paymentModeLabel, filterRowsByMode, totalsByModeFromRows } from '../../utils/paymentMode';

const TotalStateAdminCommissionReport = () => {
  const { userId } = useParams(); // State Admin User ID from URL params
  const [startDate, setStartDate] = useState(
    new Date(new Date().setDate(new Date().getDate() - 90))
  );
  const [endDate, setEndDate] = useState(new Date());
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [shops, setShops] = useState([]);
  const [selectedShop, setSelectedShop] = useState('');
  const [summary, setSummary] = useState({
    totalTransactions: 0,
    totalAmount: 0,
    totalStateAdminCommission: 0,
    totalIncome: 0,
    averageCommissionPercentage: 0
  });
  const [stateAdminInfo, setStateAdminInfo] = useState({
    userLevel: null,
    bypassRestrictions: false,
    totalShops: 0,
    isFiltered: false
  });
  const [loading, setLoading] = useState(false);
  const [shopsLoading, setShopsLoading] = useState(false);
  const [paymentMode, setPaymentMode] = useState(PAYMENT_MODES.ALL); // [CASH-MODE]
  const [totalsByMode, setTotalsByMode] = useState(null); // [CASH-MODE]

  const componentRef = useRef();

  // Get current user token for headers
  const token = sessionStorage.getItem('token');

  // Check if user level is 2 to hide certain elements
  const isUserLevel2 = stateAdminInfo.userLevel === 2;

  useEffect(() => {
    if (userId) {
      fetchShopsUnderStateAdmin();
      fetchCommissionData();
    } else {
      setDialogMessage('State Admin User ID is missing from the URL. Please check the URL and try again.');
      setOpenDialog(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Fetch commission data when dates or shop selection changes
  useEffect(() => {
    if (userId) {
      fetchCommissionData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, selectedShop, paymentMode]); // [CASH-MODE] paymentMode

  const handleStartDateChange = (date) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleShopChange = (event) => {
    setSelectedShop(event.target.value);
  };

  const handleResetFilter = () => {
    setSelectedShop('');
  };

  const validateDates = () => {
    if (!startDate || !endDate) {
      setDialogMessage('Please select both start date and end date.');
      setOpenDialog(true);
      return false;
    }
    const diffInDays = Math.ceil(
      (endDate - startDate) / (1000 * 60 * 60 * 24)
    );
    if (startDate > endDate) {
      setDialogMessage('Start date must be on or before the end date.');
      setOpenDialog(true);
      return false;
    }
    if (diffInDays > 31) {
      setDialogMessage('Date range cannot be more than 31 days.');
      setOpenDialog(true);
      return false;
    }
    return true;
  };

  const fetchShopsUnderStateAdmin = async () => {
    setShopsLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/stateAdminReport/getShopsUnderStateAdmin/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 200 && response.data.status === 'SUCCESS') {
        setShops(response.data.data.shops || []);
      }
    } catch (error) {
      console.error('Error fetching shops:', error);
      setDialogMessage('Failed to fetch shops under State Admin supervision.');
      setOpenDialog(true);
    }
    setShopsLoading(false);
  };

  const fetchCommissionData = async () => {
    if (!validateDates()) {
      return;
    }
    
    setLoading(true);
    
    try {
      const formattedStartDate = format(startDate, 'yyyy-MM-dd');
      const formattedEndDate = format(endDate, 'yyyy-MM-dd');
      
      const requestData = {
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        ...(selectedShop && { shopId: selectedShop }),
        paymentMode: paymentModeParam(paymentMode) // [CASH-MODE]
      };
      
      const apiUrl = `${API_BASE_URL}/stateAdminReport/getCommissions/${userId}`;
      
      const response = await axios.post(
        apiUrl,
        requestData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      
      if (response.status === 200 && response.data.status === 'SUCCESS') {
        const {
          transactionsList,
          summary: responseSummary,
          userLevel,
          bypassRestrictions,
          totalShops,
          isFiltered
        } = response.data.data;
        
        // [CASH-MODE] client-side guard in case the API ignores the filter
        setTransactions(filterRowsByMode(transactionsList || [], paymentMode));
        setTotalsByMode(response.data.data.totalsByMode || (responseSummary && responseSummary.totalsByMode) || null);
        setSummary(responseSummary || {
          totalTransactions: 0,
          totalAmount: 0,
          totalStateAdminCommission: 0,
          totalIncome: 0,
          averageCommissionPercentage: 0
        });
        setStateAdminInfo({
          userLevel,
          bypassRestrictions,
          totalShops: totalShops || 0,
          isFiltered
        });
      } else {
        console.error('Failed to fetch commission data');
        setTransactions([]);
        setSummary({
          totalTransactions: 0,
          totalAmount: 0,
          totalStateAdminCommission: 0,
          totalIncome: 0,
          averageCommissionPercentage: 0
        });
      }
    } catch (error) {
      console.error('=== API Call Error ===');
      console.error('Error object:', error);
      console.error('Error response:', error.response?.data);
      
      if (error.response) {
        setDialogMessage(
          error.response.data.message ||
            'An error occurred while fetching commission data.'
        );
        setOpenDialog(true);
      } else if (error.request) {
        setDialogMessage('No response from server. Please check if the backend is running.');
        setOpenDialog(true);
      } else {
        setDialogMessage('Error setting up the request: ' + error.message);
        setOpenDialog(true);
      }
      setTransactions([]);
      setSummary({
        totalTransactions: 0,
        totalAmount: 0,
        totalStateAdminCommission: 0,
        totalIncome: 0,
        averageCommissionPercentage: 0
      });
    }
    
    setLoading(false);
  };

  const handlePrint = async () => {
    if (transactions.length === 0) {
      setDialogMessage('No commission data to print.');
      setOpenDialog(true);
      return;
    }

    try {
      const input = componentRef.current;
      const canvas = await html2canvas(input, {
        scale: 2,
        logging: false,
        useCORS: true,
        windowWidth: 1400,
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('l', 'pt', 'a4');
      const imgWidth = pdf.internal.pageSize.getWidth();
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      pdf.save(`StateAdmin_CommissionReport_${selectedShop ? 'Filtered' : 'All'}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      setDialogMessage('An error occurred while generating the PDF.');
      setOpenDialog(true);
    }
  };

  // Define columns based on user level
  const getColumns = () => {
    const baseColumns = [
      { field: 'serialNumber', headerName: 'S.No.', width: 70 },
      { 
        field: 'transactionDate', 
        headerName: 'Date', 
        width: 110,
        renderCell: (params) => format(new Date(params.value), 'dd-MM-yyyy')
      },
      { field: 'businessName', headerName: 'Business Name', width: 150 },
      { field: 'sellerName', headerName: 'Seller Name', width: 120 },
      { field: 'city', headerName: 'City', width: 100 },
      // [CASH-MODE]
      { field: 'paymentMode', headerName: 'Mode', width: 90, renderCell: (params) => <PaymentModeChip row={params.row} /> },
      { 
        field: 'totalAmount', 
        headerName: 'Total Amount (₹)', 
        width: 130,
        renderCell: (params) => `₹${params.value.toFixed(2)}`
      },
    ];

    // Add conditional columns only if user level is not 2
    if (!isUserLevel2) {
      baseColumns.push(
        { 
          field: 'commissionPercentage', 
          headerName: 'Commission %', 
          width: 110,
          renderCell: (params) => `${params.value.toFixed(2)}%`
        },
        { 
          field: 'totalIncome', 
          headerName: 'Total Income (₹)', 
          width: 130,
          renderCell: (params) => `₹${params.value.toFixed(2)}`
        }
      );
    }

    // Add remaining columns
    baseColumns.push(
      { 
        field: 'stateAdminAmount', 
        headerName: 'State Admin Amount (₹)', 
        width: 150,
        renderCell: (params) => `₹${params.value.toFixed(2)}`,
        cellClassName: 'state-admin-amount-cell'
      },
      { field: 'vpaId', headerName: 'VPA ID', width: 180 }
    );

    return baseColumns;
  };

  const rows = transactions.map((transaction) => ({
    id: transaction.id,
    serialNumber: transaction.serialNumber,
    transactionDate: transaction.transactionDate,
    businessName: transaction.businessName,
    sellerName: transaction.sellerName,
    city: transaction.city,
    totalAmount: transaction.totalAmount,
    commissionPercentage: transaction.commissionPercentage,
    totalIncome: transaction.totalIncome,
    stateAdminAmount: transaction.stateAdminAmount,
    vpaId: transaction.vpaId,
    paymentMode: transaction.paymentMode // [CASH-MODE]
  }));

  // [CASH-MODE] prefer API totals, fall back to loaded rows
  const collectionByMode = totalsByMode || totalsByModeFromRows(rows, (r) => r.totalAmount);
  const collectionKey = collectionByMode?.UPI?.totalAmount !== undefined ? 'totalAmount' : 'amount';
  const stateByMode = totalsByMode && totalsByMode.UPI && totalsByMode.UPI.totalStateAdminCommission !== undefined
    ? totalsByMode
    : totalsByModeFromRows(rows, (r) => r.stateAdminAmount);
  const stateKey = stateByMode?.UPI?.totalStateAdminCommission !== undefined ? 'totalStateAdminCommission' : 'amount';

  const renderTableRows = () => {
    return rows.map((row) => (
      <tr key={row.id}>
        <td>{row.serialNumber}</td>
        <td>{format(new Date(row.transactionDate), 'dd-MM-yyyy')}</td>
        <td>{row.businessName}</td>
        <td>{row.sellerName}</td>
        <td>{row.city}</td>
        <td>{paymentModeLabel(row)}</td>{/* [CASH-MODE] */}
        <td>₹{row.totalAmount.toFixed(2)}</td>
        {!isUserLevel2 && <td>{row.commissionPercentage.toFixed(2)}%</td>}
        {!isUserLevel2 && <td>₹{row.totalIncome.toFixed(2)}</td>}
        <td style={{ fontWeight: 'bold', color: '#f57c00' }}>₹{row.stateAdminAmount.toFixed(2)}</td>
        <td>{row.vpaId}</td>
      </tr>
    ));
  };

  const getSelectedShopName = () => {
    if (!selectedShop) return '';
    const shop = shops.find(s => s.shopId === selectedShop);
    return shop ? shop.businessName : '';
  };

  // Define summary cards based on user level
  const getSummaryCards = () => {
    const cards = [];
    
    // Always show Total Transactions
    cards.push(
      <Grid item xs={12} sm={isUserLevel2 ? 4 : 2.4} key="totalTransactions">
        <Paper elevation={3} style={{ padding: '15px', textAlign: 'center' }}>
          <Typography variant="h6" color="warning.main">
            Total Transactions
          </Typography>
          <Typography variant="h4">
            {summary.totalTransactions}
          </Typography>
        </Paper>
      </Grid>
    );

    // Always show Total Amount
    cards.push(
      <Grid item xs={12} sm={isUserLevel2 ? 4 : 2.4} key="totalAmount">
        <Paper elevation={3} style={{ padding: '15px', textAlign: 'center' }}>
          <Typography variant="h6" color="warning.main">
            Total Amount
          </Typography>
          <Typography variant="h5">
            ₹{summary.totalAmount.toFixed(2)}
          </Typography>
        </Paper>
      </Grid>
    );

    // Only show these cards if user level is not 2
    if (!isUserLevel2) {
      cards.push(
        <Grid item xs={12} sm={2.4} key="avgCommission">
          <Paper elevation={3} style={{ padding: '15px', textAlign: 'center' }}>
            <Typography variant="h6" color="warning.main">
              Avg Commission %
            </Typography>
            <Typography variant="h5">
              {summary.averageCommissionPercentage.toFixed(2)}%
            </Typography>
          </Paper>
        </Grid>
      );

      cards.push(
        <Grid item xs={12} sm={2.4} key="totalIncome">
          <Paper elevation={3} style={{ padding: '15px', textAlign: 'center' }}>
            <Typography variant="h6" color="warning.main">
              Total Income
            </Typography>
            <Typography variant="h5">
              ₹{summary.totalIncome.toFixed(2)}
            </Typography>
          </Paper>
        </Grid>
      );
    }

    // Always show State Admin Commission
    cards.push(
      <Grid item xs={12} sm={isUserLevel2 ? 4 : 2.4} key="stateAdminCommission">
        <Paper elevation={3} style={{ padding: '15px', textAlign: 'center', backgroundColor: '#fff8e1', border: '2px solid #f57c00' }}>
          <Typography variant="h6" style={{ color: '#f57c00', fontWeight: 'bold' }}>
            Total State Admin Commission
          </Typography>
          <Typography variant="h4" style={{ color: '#f57c00', fontWeight: 'bold' }}>
            ₹{summary.totalStateAdminCommission.toFixed(2)}
          </Typography>
        </Paper>
      </Grid>
    );

    return cards;
  };

  return (
    <Box sx={{ position: 'relative', padding: 2 }}>
      {/* Place BackButton outside the Grid */}
      <BackButton />
      <br />  <br />
      
      {/* Custom CSS for State Admin Amount column */}
      <style>
        {`
          .state-admin-amount-cell {
            font-weight: bold !important;
            color: #f57c00 !important;
            background-color: #fff8e1 !important;
          }
        `}
      </style>
      
      {/* Loading Backdrop */}
      <Backdrop 
        sx={{ 
          color: '#fff', 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          flexDirection: 'column',
          gap: 2
        }} 
        open={loading}
      >
        <CircularProgress color="inherit" size={60} />
        <Typography variant="h6" align="center">
          Processing State Admin Commission Data...
        </Typography>
        <Typography variant="body2" align="center" sx={{ maxWidth: 400 }}>
          {selectedShop ? 
            `Analyzing commission data for selected shop...` :
            `Analyzing commission data from all shops under State Admin supervision...`
          }
        </Typography>
      </Backdrop>

      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <div style={{ padding: '20px' }}>
          <Typography variant="h6" style={{ marginTop: '10px' }}>
            State Admin Commission Report {stateAdminInfo.isFiltered ? '(Filtered)' : '(All Shops)'}
          </Typography>
          
          {/* State Admin Info */}
          {stateAdminInfo.userLevel && (
            <Typography variant="body2" style={{ marginTop: '5px', color: 'gray' }}>
              User Level: {stateAdminInfo.userLevel} | 
              {stateAdminInfo.bypassRestrictions ? ' All System Access' : ' Pincode Restricted'} | 
              Total Shops: {stateAdminInfo.totalShops}
              {stateAdminInfo.isFiltered && ` | Showing: 1 shop`}
            </Typography>
          )}

          {/* Active Filter Display */}
          {selectedShop && (
            <Box sx={{ marginTop: 1, marginBottom: 2 }}>
              <Chip 
                label={`Filtered by: ${getSelectedShopName()}`}
                color="warning" 
                variant="outlined"
                onDelete={handleResetFilter}
                sx={{ marginRight: 1 }}
              />
            </Box>
          )}

          {/* Info Alert */}
          <Alert severity="info" sx={{ marginTop: 2, marginBottom: 2 }}>
            {selectedShop ? 
              `Showing commission data from selected shop only. Use the shop filter to change or reset to see all shops.` :
              `Showing commission data from ALL shops under State Admin supervision. Use the shop filter to view specific shop data.`
            }
          </Alert>

          <Grid
            container
            spacing={2}
            alignItems="center"
            style={{ marginTop: '20px' }}
          >
            <Grid item xs={12} sm={3}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={handleStartDateChange}
                renderInput={(params) => <TextField fullWidth {...params} />}
                maxDate={new Date()}
                inputFormat="dd-MM-yyyy"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={handleEndDateChange}
                renderInput={(params) => <TextField fullWidth {...params} />}
                maxDate={new Date()}
                inputFormat="dd-MM-yyyy"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth disabled={shopsLoading}>
                <InputLabel>Filter by Shop</InputLabel>
                <Select
                  value={selectedShop}
                  label="Filter by Shop"
                  onChange={handleShopChange}
                >
                  <MenuItem value="">
                    <em>All Shops</em>
                  </MenuItem>
                  {shops.map((shop) => (
                    <MenuItem key={shop.shopId} value={shop.shopId}>
                      {shop.businessName} - {shop.city}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button
                variant="contained"
                color="warning"
                onClick={fetchCommissionData}
                style={{ marginRight: '10px' }}
                disabled={loading}
                fullWidth
              >
                {loading ? 'Loading...' : 'Refresh'}
              </Button>
            </Grid>
          </Grid>

          {/* Additional Controls */}
          <Grid container spacing={2} style={{ marginTop: '10px' }}>
            <Grid item xs={12} sm={6} sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              {/* [CASH-MODE] */}
              <PaymentModeFilter value={paymentMode} onChange={setPaymentMode} />
              {selectedShop && (
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleResetFilter}
                  disabled={loading}
                >
                  Reset Filter (Show All Shops)
                </Button>
              )}
            </Grid>
            <Grid item xs={12} sm={6} style={{ textAlign: 'right' }}>
              <Button
                variant="contained"
                color="secondary"
                onClick={handlePrint}
                disabled={transactions.length === 0 || loading}
              >
                Print Report
              </Button>
            </Grid>
          </Grid>

          {/* Summary Cards - Conditional based on user level */}
          {transactions.length > 0 && (
            <Grid container spacing={2} style={{ marginTop: '20px' }}>
              {getSummaryCards()}
            </Grid>
          )}
          {/* [CASH-MODE] */}
          {transactions.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <PaymentModeTotals totals={collectionByMode} amountKey={collectionKey} title="Collection" />
              <PaymentModeTotals totals={stateByMode} amountKey={stateKey} title="State Admin Commission" />
            </Box>
          )}

          {!loading && (
            <div style={{ marginTop: '20px', height: 600, width: '100%' }}>
              {transactions.length > 0 ? (
                <DataGrid
                  rows={rows}
                  columns={getColumns()}
                  pageSize={25}
                  rowsPerPageOptions={[25, 50, 100]}
                  disableSelectionOnClick
                  loading={loading}
                  pagination
                  paginationMode="client"
                />
              ) : (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <Typography variant="h6" align="center">
                    {selectedShop ? 
                      `No commission data found for the selected shop in the chosen date range.` :
                      `No commission data found for the selected dates.`
                    }
                  </Typography>
                  {stateAdminInfo.totalShops > 0 && (
                    <Alert severity="info" sx={{ marginTop: 2, maxWidth: 600, margin: '16px auto' }}>
                      <Typography variant="body2">
                        <strong>State Admin Info:</strong> You have access to {stateAdminInfo.totalShops} shops. 
                        {selectedShop ? 
                          ' Try expanding your date range or select a different shop.' :
                          ' Try expanding your date range to include more commission data.'
                        }
                      </Typography>
                    </Alert>
                  )}
                </div>
              )}
            </div>
          )}

          <Dialog open={openDialog} onClose={handleDialogClose}>
            <DialogTitle>Message</DialogTitle>
            <DialogContent>
              <Typography>{dialogMessage}</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleDialogClose} color="primary">
                OK
              </Button>
            </DialogActions>
          </Dialog>

          {/* Printable Component */}
          <div style={{ position: 'absolute', left: '-10000px', top: 0 }}>
            <div ref={componentRef} style={{ width: '1400px', padding: '20px' }}>
              <Typography variant="h4" align="center">
                Q-Pay Reports
              </Typography>
              <Typography
                variant="h6"
                align="center"
                style={{ marginTop: '10px' }}
              >
                State Admin Commission Report {selectedShop ? '(Filtered by Shop)' : '(All Shops)'}
              </Typography>
              <Typography
                variant="subtitle1"
                align="center"
                style={{ marginBottom: '10px' }}
              >
                {`From ${format(startDate, 'dd-MM-yyyy')} to ${format(
                  endDate,
                  'dd-MM-yyyy'
                )}`}
              </Typography>
              {selectedShop && (
                <Typography
                  variant="subtitle2"
                  align="center"
                  style={{ marginBottom: '20px' }}
                >
                  Filtered by Shop: {getSelectedShopName()}
                </Typography>
              )}
              
              {/* Summary for Print - Conditional based on user level */}
              <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                <Typography variant="h6">Commission Summary</Typography>
                <Typography>
                  Total Transactions: {summary.totalTransactions} | Total Amount: ₹{summary.totalAmount.toFixed(2)}
                  {!isUserLevel2 && ` | Average Commission: ${summary.averageCommissionPercentage.toFixed(2)}% | Total Income: ₹${summary.totalIncome.toFixed(2)}`}
                </Typography>
                <Typography style={{ fontWeight: 'bold', color: '#f57c00' }}>
                  Total State Admin Commission: ₹{summary.totalStateAdminCommission.toFixed(2)}
                </Typography>
                {/* [CASH-MODE] */}
                <Typography>
                  UPI: ₹{(Number(collectionByMode?.UPI?.[collectionKey]) || 0).toFixed(2)} | Cash: ₹{(Number(collectionByMode?.CASH?.[collectionKey]) || 0).toFixed(2)}
                </Typography>
              </div>

              {transactions.length > 0 && (
                <table className="print-table" border="1" cellPadding="5">
                  <thead>
                    <tr>
                      <th>S.No.</th>
                      <th>Date</th>
                      <th>Business Name</th>
                      <th>Seller Name</th>
                      <th>City</th>
                      <th>Mode</th>{/* [CASH-MODE] */}
                      <th>Total Amount (₹)</th>
                      {!isUserLevel2 && <th>Commission %</th>}
                      {!isUserLevel2 && <th>Total Income (₹)</th>}
                      <th style={{ backgroundColor: '#f1f8e9', fontWeight: 'bold' }}>State Admin Amount (₹)</th>
                      <th>VPA ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {renderTableRows()}
                  </tbody>
                </table>
              )}
              
              <div style={{ marginTop: '20px', textAlign: 'center' }}>
                <Typography variant="h6" style={{ color: '#2e7d32', fontWeight: 'bold' }}>
                  Grand Total State Admin Commission: ₹{summary.totalStateAdminCommission.toFixed(2)}
                </Typography>
              </div>
            </div>
          </div>
        </div>
      </LocalizationProvider>
    </Box>
  );
};

export default TotalStateAdminCommissionReport;